<script setup>
import {computed, onMounted, onUnmounted, ref} from 'vue';
import {usePage} from '@inertiajs/vue3';
import BitbucketIcon from '@/Components/SocialstreamIcons/BitbucketIcon.vue';
import FacebookIcon from '@/Components/SocialstreamIcons/FacebookIcon.vue';
import GithubIcon from '@/Components/SocialstreamIcons/GithubIcon.vue';
import GitLabIcon from '@/Components/SocialstreamIcons/GitLabIcon.vue';
import GoogleIcon from '@/Components/SocialstreamIcons/GoogleIcon.vue';
import InputError from '@/Components/InputError.vue';
import LinkedInIcon from '@/Components/SocialstreamIcons/LinkedInIcon.vue';
import TwitterIcon from '@/Components/SocialstreamIcons/TwitterIcon.vue';
import {route} from "ziggy-js";
import YandexIcon from "@/Components/SocialstreamIcons/YandexIcon.vue";
import VkIcon from "@/Components/SocialstreamIcons/VkIcon.vue";
import MailruIcon from "@/Components/SocialstreamIcons/MailruIcon.vue";

const page = usePage();
const error = computed(() => page.props.errors.socialstream);
const providerIds = computed(() => page.props.socialstream.providers.map(provider => provider.id));

// The provider links are plain navigations to `/oauth/{provider}`, and each one writes a
// fresh `state` into the session, overwriting whatever the previous attempt stored. On a
// slow mobile connection the page stays interactive while the redirect is in flight, so an
// impatient user taps again — on 2026-07-27 one user fired ten `/oauth/google` redirects in
// 13 seconds. Every callback then carrying a state other than the last one written fails
// Socialite's check and dumps the user back on the login page (see App\Actions\Socialstream\
// HandleInvalidState). One attempt at a time is all the OAuth flow can represent, so ignore
// clicks once one is under way.
const redirecting = ref(false);

const startRedirect = (event) => {
    // The handler sits on the container, so it also sees clicks that landed on the gaps
    // between the buttons. Those navigate nowhere and must not lock the buttons down.
    if (!event.target.closest('a[href]')) {
        return;
    }

    if (redirecting.value) {
        event.preventDefault();

        return;
    }

    redirecting.value = true;
};

// A browser returning to this page from the back/forward cache keeps the component's state,
// which would leave the buttons permanently dead for a user who backed out of the consent
// screen. `pageshow` with `persisted` is the only event fired in that case.
onMounted(() => window.addEventListener('pageshow', onPageShow));
onUnmounted(() => window.removeEventListener('pageshow', onPageShow));

function onPageShow(event) {
    if (event.persisted) {
        redirecting.value = false;
    }
}
</script>

<template>
    <div class="border-t border-dashed px-2">
        <div class="flex flex-row dark:text-slate-200 items-center justify-center py-4 text-sm">
            <!--      <hr class="w-full mr-2">-->
            {{ $t('or login with') }}
            <!--      <hr class="w-full ml-2">-->
        </div>

        <!-- One delegated handler rather than one per provider: a click on any of the links
             below bubbles here before the browser follows the href, so cancelling it here
             cancels the navigation. -->
        <div class="flex flex-col items-center justify-center"
             :class="{ 'opacity-60 pointer-events-none': redirecting }"
             @click="startRedirect">
            <a v-if="providerIds.includes('facebook')"
               :href="route('oauth.redirect', 'facebook')">
                <FacebookIcon class="h-6 w-6 mx-2"/>
                <span class="sr-only dark:text-slate-200">Facebook</span>
            </a>
            <a v-if="providerIds.includes('google')"
               :href="route('oauth.redirect', 'google')"
               class="w-full bg-slate-200 dark:bg-slate-800 h-10 flex items-center justify-center rounded-full hover:bg-slate-300 dark:hover:bg-slate-700 transition-colors">
                <div class="w-[90px] whitespace-nowrap">
                    <GoogleIcon class="h-6 w-6 mx-2 inline"/>
                    <span class="dark:text-slate-200">Google</span>
                </div>
            </a>

            <a v-if="providerIds.includes('yandex')"
               :href="route('oauth.redirect', 'yandex')"
               class="w-full bg-slate-200 dark:bg-slate-800 h-10 flex items-center justify-center rounded-full mt-3 hover:bg-slate-300 dark:hover:bg-slate-700 transition-colors">
                <div class="w-[90px] whitespace-nowrap">
                    <YandexIcon class="h-6 w-6 mx-2 inline"/>
                    <span class="dark:text-slate-200">Yandex</span>
                </div>
            </a>
            <a v-if="providerIds.includes('vkontakte')"
               :href="route('oauth.redirect', 'vkontakte')"
               class="w-full bg-slate-200 dark:bg-slate-800 h-10 flex items-center justify-center rounded-full mt-3 hover:bg-slate-300 dark:hover:bg-slate-700 transition-colors">
                <div class="w-[90px] whitespace-nowrap">
                    <VkIcon class="h-6 w-6 mx-2 inline"/>
                    <span class="dark:text-slate-200">{{ $t('VK') }}</span>
                </div>
            </a>
            <a v-if="providerIds.includes('mailru')"
               :href="route('oauth.redirect', 'mailru')"
               class="w-full bg-slate-200 dark:bg-slate-800 h-10 flex items-center justify-center rounded-full mt-3 hover:bg-slate-300 dark:hover:bg-slate-700 transition-colors">
                <div class="w-[90px] whitespace-nowrap">
                    <MailruIcon class="h-6 w-6 mx-2 inline"/>
                    <span class="dark:text-slate-200">Mail.ru</span>
                </div>
            </a>

            <a
                v-if="providerIds.includes('twitter')"
                :href="route('oauth.redirect', 'twitter')">
                <TwitterIcon class="h-6 w-6 mx-2"/>
                <span class="sr-only">Twitter</span>
            </a>

            <a
                v-if="providerIds.includes('twitter-oauth-2')"
                :href="route('oauth.redirect', 'twitter-oauth-2')">
                <TwitterIcon class="h-6 w-6 mx-2"/>
                <span class="sr-only">Twitter</span>
            </a>

            <a v-if="providerIds.includes('linkedin')"
               :href="route('oauth.redirect', 'linkedin')">
                <LinkedInIcon class="h-6 w-6 mx-2"/>
                <span class="sr-only">LinkedIn</span>
            </a>

            <a v-if="providerIds.includes('github')" :href="route('oauth.redirect', 'github')">
                <GithubIcon class="h-6 w-6 mx-2"/>
                <span class="sr-only">GitHub</span>
            </a>

            <a v-if="providerIds.includes('gitlab')" :href="route('oauth.redirect', 'gitlab')">
                <GitLabIcon class="h-6 w-6 mx-2"/>
                <span class="sr-only">GitLab</span>
            </a>

            <a v-if="providerIds.includes('bitbucket')"
               :href="route('oauth.redirect', 'bitbucket')">
                <BitbucketIcon class="h-6 w-6 mx-2"/>
                <span class="sr-only">BitBucket</span>
            </a>
        </div>

        <InputError :message="error" class="mt-b text-center"/>
    </div>
</template>
